/**
 * Training Center Page
 * Interactive training modules with AI voice narration and quizzes
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  Loader2,
  GraduationCap,
  Clock,
  CheckCircle,
  ChevronRight,
  LayoutDashboard,
  Users,
  UserCog,
  Calendar,
  Fingerprint,
  FileText,
  Smartphone,
  DollarSign,
  MessageSquare,
  Settings,
  BookOpen,
  Headphones,
  ClipboardCheck,
  XCircle,
  Trophy,
  AlertCircle,
  RefreshCw,
  Image,
  Video,
  Download,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Module icons mapping
const moduleIcons = {
  dashboard: LayoutDashboard,
  clients: Users,
  employees: UserCog,
  scheduling: Calendar,
  evv: Fingerprint,
  referrals: FileText,
  caregiver_portal: Smartphone,
  hr_payroll: DollarSign,
  communications: MessageSquare,
  settings: Settings,
};

// Module colors mapping
const moduleColors = {
  dashboard: "bg-blue-500",
  clients: "bg-emerald-500",
  employees: "bg-purple-500",
  scheduling: "bg-amber-500",
  evv: "bg-cyan-500",
  referrals: "bg-rose-500",
  caregiver_portal: "bg-teal-500",
  hr_payroll: "bg-indigo-500",
  communications: "bg-orange-500",
  settings: "bg-slate-500",
};

export default function TrainingPage() {
  const { token, user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleDetails, setModuleDetails] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("nova");  // Default: Nova (energetic)
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [audioData, setAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [trainingProgress, setTrainingProgress] = useState({});
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  // Video state (Sora 2 AI-generated walkthrough clips)
  const [videoStatus, setVideoStatus] = useState(null); // { status, video_url, generated_at, error, duration, size }
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoTriggering, setVideoTriggering] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const videoPollRef = useRef(null);
  
  const audioRef = useRef(null);

  // Fetch modules and progress on mount
  useEffect(() => {
    fetchModules();
    fetchVoices();
    fetchTrainingProgress();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await fetch(`${API}/api/training/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setModules(data);
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
      toast.error("Failed to load training modules");
    } finally {
      setLoading(false);
    }
  };

  const fetchVoices = async () => {
    try {
      const res = await fetch(`${API}/api/training/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVoices(data.voices || []);
      }
    } catch (err) {
      console.error("Error fetching voices:", err);
    }
  };

  const fetchTrainingProgress = async () => {
    try {
      const res = await fetch(`${API}/api/training/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrainingProgress(data);
      }
    } catch (err) {
      console.error("Error fetching progress:", err);
    }
  };

  const fetchModuleDetails = async (moduleId) => {
    try {
      const res = await fetch(`${API}/api/training/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setModuleDetails(data);
      }
    } catch (err) {
      console.error("Error fetching module details:", err);
    }
  };

  const selectModule = async (moduleId) => {
    setSelectedModule(moduleId);
    setAudioData(null);
    setProgress(0);
    setIsPlaying(false);
    setShowQuiz(false);
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    setVideoStatus(null);
    if (videoBlobUrl) {
      URL.revokeObjectURL(videoBlobUrl);
      setVideoBlobUrl(null);
    }
    stopVideoPolling();
    await fetchModuleDetails(moduleId);
    fetchVideoStatus(moduleId);
  };

  // ---- Sora 2 video walkthrough helpers ----
  const stopVideoPolling = () => {
    if (videoPollRef.current) {
      clearInterval(videoPollRef.current);
      videoPollRef.current = null;
    }
  };

  const fetchVideoStatus = async (moduleId) => {
    if (!moduleId) return;
    setVideoLoading(true);
    try {
      const res = await fetch(`${API}/api/training/modules/${moduleId}/video-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVideoStatus(data);
        // Start polling if generation is in-flight
        if (data.status === "generating") {
          startVideoPolling(moduleId);
        }
      }
    } catch (err) {
      console.error("Error fetching video status:", err);
    } finally {
      setVideoLoading(false);
    }
  };

  const startVideoPolling = (moduleId) => {
    stopVideoPolling();
    videoPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/training/modules/${moduleId}/video-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setVideoStatus(data);
        if (data.status !== "generating") {
          stopVideoPolling();
          if (data.status === "ready") {
            toast.success("Training video is ready to play!");
          } else if (data.status === "failed") {
            toast.error(`Video generation failed: ${data.error || "Unknown error"}`);
          }
        }
      } catch (err) {
        console.error("Video poll error:", err);
      }
    }, 15000);
  };

  const triggerVideoGeneration = async (force = false) => {
    if (!selectedModule) return;
    setVideoTriggering(true);
    try {
      const res = await fetch(`${API}/api/training/modules/${selectedModule}/generate-video`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ size: "1280x720", duration: 12, force }),
      });
      const data = await res.json();
      if (res.ok) {
        setVideoStatus(data);
        toast.success("Video generation started. This takes 2-5 minutes.");
        startVideoPolling(selectedModule);
      } else {
        toast.error(data.detail || "Failed to start video generation");
      }
    } catch (err) {
      console.error("Error triggering video generation:", err);
      toast.error("Failed to start video generation");
    } finally {
      setVideoTriggering(false);
    }
  };

  const downloadVideo = () => {
    if (!selectedModule || videoStatus?.status !== "ready") return;
    const url = `${API}/api/training/modules/${selectedModule}/video/download`;
    // Use a fetch + blob to attach the Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${selectedModule}-training.mp4`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error("Download failed"));
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopVideoPolling();
  }, []);

  // Auto-fetch video blob once status flips to "ready"
  useEffect(() => {
    if (!selectedModule) return;
    if (videoStatus?.status !== "ready") return;
    if (videoBlobUrl) return; // already loaded

    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/training/modules/${selectedModule}/video`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        setVideoBlobUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.error("Failed to load video blob:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoStatus?.status, selectedModule]);

  const generateAudio = async () => {
    if (!selectedModule) return;
    
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/training/generate-audio`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          module_id: selectedModule,
          voice_id: selectedVoice
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAudioData(data.audio_base64);
        toast.success("Audio generated successfully!");
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to generate audio");
      }
    } catch (err) {
      console.error("Error generating audio:", err);
      toast.error("Failed to generate audio");
    } finally {
      setGenerating(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const restartAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setProgress(100);
    toast.success("Audio completed! Ready for the quiz?");
  };

  // Quiz functions
  const startQuiz = async () => {
    if (!selectedModule) return;
    
    try {
      const res = await fetch(`${API}/api/training/modules/${selectedModule}/quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuizData(data);
        setQuizAnswers({});
        setQuizResult(null);
        setShowQuiz(true);
      } else {
        toast.error("Failed to load quiz");
      }
    } catch (err) {
      console.error("Error loading quiz:", err);
      toast.error("Failed to load quiz");
    }
  };

  const selectAnswer = (questionId, answerIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const submitQuiz = async () => {
    if (!selectedModule || !quizData) return;
    
    // Check if all questions are answered
    const unanswered = quizData.questions.filter(q => quizAnswers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    
    setQuizSubmitting(true);
    try {
      const res = await fetch(`${API}/api/training/modules/${selectedModule}/quiz/submit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          module_id: selectedModule,
          answers: quizAnswers
        })
      });
      
      if (res.ok) {
        const result = await res.json();
        setQuizResult(result);
        setShowResultDialog(true);
        // Refresh progress
        fetchTrainingProgress();
      } else {
        toast.error("Failed to submit quiz");
      }
    } catch (err) {
      console.error("Error submitting quiz:", err);
      toast.error("Failed to submit quiz");
    } finally {
      setQuizSubmitting(false);
    }
  };

  const retakeQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
    setShowResultDialog(false);
  };

  // Calculate completion based on server progress
  const completedModules = trainingProgress.module_progress 
    ? Object.entries(trainingProgress.module_progress).filter(([_, p]) => p.quiz_passed).map(([id]) => id)
    : [];
  
  const completionPercentage = trainingProgress.completion_percentage || 0;

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="training-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="training-header">
              <GraduationCap className="w-7 h-7 text-primary" />
              Training Center
            </h1>
            <p className="text-slate-500 mt-1">
              Learn how to use CarePilot with interactive voice-guided tutorials
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Your Progress</p>
              <p className="text-lg font-semibold text-primary">{completionPercentage}% Complete</p>
            </div>
            <div className="w-32">
              <Progress value={completionPercentage} className="h-2" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Module List */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Training Modules
                </CardTitle>
                <CardDescription>
                  {completedModules.length} of {modules.length} completed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {modules.map((module) => {
                  const Icon = moduleIcons[module.id] || BookOpen;
                  const moduleProgress = trainingProgress.module_progress?.[module.id];
                  const isCompleted = moduleProgress?.quiz_passed;
                  const isSelected = selectedModule === module.id;
                  
                  return (
                    <button
                      key={module.id}
                      onClick={() => selectModule(module.id)}
                      data-testid={`module-btn-${module.id}`}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        isSelected 
                          ? "border-primary bg-primary/5 shadow-sm" 
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${moduleColors[module.id]} text-white`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 truncate">{module.title}</p>
                            {isCompleted && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {module.duration}
                            </p>
                            {moduleProgress?.quiz_score !== undefined && (
                              <Badge variant={isCompleted ? "default" : "secondary"} className="text-xs">
                                {moduleProgress.quiz_score}%
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isSelected ? "rotate-90" : ""}`} />
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Module Content */}
          <div className="lg:col-span-2 space-y-4">
            {selectedModule && moduleDetails ? (
              <>
                {/* Module Header */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${moduleColors[selectedModule]} text-white`}>
                          {(() => {
                            const Icon = moduleIcons[selectedModule] || BookOpen;
                            return <Icon className="w-7 h-7" />;
                          })()}
                        </div>
                        <div>
                          <CardTitle className="text-xl">{moduleDetails.title}</CardTitle>
                          <CardDescription className="mt-1">{moduleDetails.description}</CardDescription>
                        </div>
                      </div>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {moduleDetails.duration}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>

                {/* Tab-like navigation between Content and Quiz */}
                <div className="flex gap-2">
                  <Button
                    variant={!showQuiz ? "default" : "outline"}
                    onClick={() => setShowQuiz(false)}
                    className="flex-1"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Content
                  </Button>
                  <Button
                    variant={showQuiz ? "default" : "outline"}
                    onClick={startQuiz}
                    className="flex-1"
                    data-testid="take-quiz-btn"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    Take Quiz
                  </Button>
                </div>

                {!showQuiz ? (
                  <>
                    {/* AI Video Walkthrough (Sora 2) */}
                    <Card data-testid="video-walkthrough-card" className="border-purple-200 bg-gradient-to-br from-purple-50 to-slate-50">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Video className="w-5 h-5 text-purple-600" />
                            AI Video Walkthrough
                            <Badge variant="outline" className="ml-1 text-xs bg-white">
                              <Sparkles className="w-3 h-3 mr-1 text-purple-600" />
                              Sora 2
                            </Badge>
                          </CardTitle>
                          {videoStatus?.status === "ready" && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Ready
                            </Badge>
                          )}
                        </div>
                        <CardDescription>
                          Cinematic AI-generated clip that visualises this module.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {videoLoading && !videoStatus && (
                          <div className="flex items-center justify-center py-8 text-slate-500 text-sm gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Checking video status...
                          </div>
                        )}

                        {videoStatus?.status === "not_generated" && (
                          <div className="rounded-lg border border-dashed border-purple-300 bg-white p-6 text-center">
                            <Video className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                            <p className="text-sm text-slate-700 font-medium">
                              No walkthrough video yet
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {isAdmin
                                ? "Generate a ~12s cinematic clip powered by Sora 2. Takes 2–5 minutes."
                                : "Ask an administrator to generate the walkthrough video for this module."}
                            </p>
                            {isAdmin && (
                              <Button
                                className="mt-4 gap-2"
                                onClick={() => triggerVideoGeneration(false)}
                                disabled={videoTriggering}
                                data-testid="generate-video-btn"
                              >
                                {videoTriggering ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Starting...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="w-4 h-4" />
                                    Generate Video
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}

                        {videoStatus?.status === "generating" && (
                          <div className="rounded-lg border border-purple-200 bg-white p-6 text-center" data-testid="video-generating-panel">
                            <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-2" />
                            <p className="text-sm text-slate-800 font-medium">Generating your walkthrough...</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Sora 2 typically renders in 2–5 minutes. Feel free to keep browsing — we'll notify you when it's ready.
                            </p>
                          </div>
                        )}

                        {videoStatus?.status === "failed" && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-red-800">Generation failed</p>
                                <p className="text-xs text-red-700 mt-1 break-words">
                                  {videoStatus.error || "Sora 2 did not return a video."}
                                </p>
                                {isAdmin && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-3 gap-2"
                                    onClick={() => triggerVideoGeneration(true)}
                                    disabled={videoTriggering}
                                    data-testid="retry-video-btn"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {videoStatus?.status === "ready" && (
                          <div className="space-y-3">
                            <div className="rounded-lg overflow-hidden bg-black shadow-md">
                              {videoBlobUrl ? (
                                <video
                                  key={videoBlobUrl}
                                  src={videoBlobUrl}
                                  controls
                                  className="w-full h-auto"
                                  data-testid="training-video-player"
                                />
                              ) : (
                                <div className="flex items-center justify-center py-16 text-slate-300 text-sm gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Loading video...
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                              <span>
                                {videoStatus.size} · {videoStatus.duration}s
                                {videoStatus.generated_at && (
                                  <>
                                    {" "}· Generated {new Date(videoStatus.generated_at).toLocaleString()}
                                  </>
                                )}
                              </span>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={downloadVideo}
                                  disabled={!videoBlobUrl}
                                  className="gap-1"
                                  data-testid="download-video-btn"
                                >
                                  <Download className="w-3 h-3" />
                                  Download MP4
                                </Button>
                                {isAdmin && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => triggerVideoGeneration(true)}
                                    disabled={videoTriggering}
                                    className="gap-1"
                                    data-testid="regenerate-video-btn"
                                  >
                                    {videoTriggering ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3 h-3" />
                                    )}
                                    Regenerate
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Voice Selection & Generate */}
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="flex-1 min-w-[200px]">
                            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                              Select Voice
                            </label>
                            <Select value={selectedVoice} onValueChange={setSelectedVoice} data-testid="voice-select">
                              <SelectTrigger>
                                <SelectValue placeholder="Choose a voice" />
                              </SelectTrigger>
                              <SelectContent>
                                {voices.length > 0 ? (
                                  voices.map((voice) => (
                                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                      {voice.name} - {voice.description}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="nova">Nova - Energetic, upbeat voice</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="pt-6">
                            <Button 
                              onClick={generateAudio} 
                              disabled={generating}
                              className="gap-2"
                              data-testid="generate-audio-btn"
                            >
                              {generating ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Headphones className="w-4 h-4" />
                                  Generate Audio
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Audio Player */}
                    {audioData && (
                      <Card className="border-primary/30 bg-primary/5" data-testid="audio-player-card">
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                                <Volume2 className="w-5 h-5 text-primary" />
                                Audio Narration
                              </h3>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={toggleMute}
                                  className="h-8 w-8"
                                >
                                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={restartAudio}
                                  className="h-8 w-8"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <Button
                                size="lg"
                                onClick={playAudio}
                                className="h-14 w-14 rounded-full"
                                data-testid="play-pause-btn"
                              >
                                {isPlaying ? (
                                  <Pause className="w-6 h-6" />
                                ) : (
                                  <Play className="w-6 h-6 ml-1" />
                                )}
                              </Button>
                              <div className="flex-1">
                                <Progress value={progress} className="h-2" />
                                <p className="text-xs text-slate-500 mt-1">
                                  {isPlaying ? "Playing..." : progress >= 100 ? "Completed" : "Click play to start"}
                                </p>
                              </div>
                            </div>
                            
                            <audio
                              ref={audioRef}
                              src={`data:audio/mpeg;base64,${audioData}`}
                              onTimeUpdate={handleAudioTimeUpdate}
                              onEnded={handleAudioEnded}
                              onPlay={() => setIsPlaying(true)}
                              onPause={() => setIsPlaying(false)}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Module Visual */}
                    {moduleDetails.image && (
                      <Card data-testid="module-visual-card">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Image className="w-5 h-5" />
                            Visual Guide
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                            <img 
                              src={moduleDetails.image} 
                              alt={`${moduleDetails.title} visual guide`}
                              className="w-full h-auto object-cover"
                              loading="lazy"
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-2 text-center">
                            Visual representation of the {moduleDetails.title} interface
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Script/Transcript */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Transcript</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-slate prose-sm max-w-none">
                          {moduleDetails.script.split('\n\n').map((paragraph, idx) => (
                            <p key={idx} className="text-slate-600 leading-relaxed mb-4">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  /* Quiz Section */
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5 text-primary" />
                        Knowledge Check
                      </CardTitle>
                      <CardDescription>
                        Answer all questions correctly to complete this module. You need {quizData?.passing_score}% to pass.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {quizData?.questions.map((question, qIdx) => (
                        <div key={question.id} className="space-y-3" data-testid={`quiz-question-${question.id}`}>
                          <p className="font-medium text-slate-900">
                            {qIdx + 1}. {question.question}
                          </p>
                          <div className="space-y-2">
                            {question.options.map((option, oIdx) => {
                              const isSelected = quizAnswers[question.id] === oIdx;
                              const showResults = quizResult !== null;
                              const questionResult = quizResult?.results?.find(r => r.question_id === question.id);
                              const isCorrect = questionResult?.correct_answer === oIdx;
                              const wasUserAnswer = questionResult?.user_answer === oIdx;
                              
                              let optionClass = "border-slate-200 hover:border-slate-300 hover:bg-slate-50";
                              if (showResults) {
                                if (isCorrect) {
                                  optionClass = "border-green-500 bg-green-50";
                                } else if (wasUserAnswer && !questionResult?.is_correct) {
                                  optionClass = "border-red-500 bg-red-50";
                                }
                              } else if (isSelected) {
                                optionClass = "border-primary bg-primary/5";
                              }
                              
                              return (
                                <button
                                  key={oIdx}
                                  onClick={() => !showResults && selectAnswer(question.id, oIdx)}
                                  disabled={showResults}
                                  className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${optionClass}`}
                                  data-testid={`quiz-option-${question.id}-${oIdx}`}
                                >
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    showResults && isCorrect ? "border-green-500 bg-green-500" :
                                    showResults && wasUserAnswer && !questionResult?.is_correct ? "border-red-500 bg-red-500" :
                                    isSelected ? "border-primary bg-primary" : "border-slate-300"
                                  }`}>
                                    {showResults && isCorrect && <CheckCircle className="w-4 h-4 text-white" />}
                                    {showResults && wasUserAnswer && !questionResult?.is_correct && <XCircle className="w-4 h-4 text-white" />}
                                    {!showResults && isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                  </div>
                                  <span className={`${showResults && isCorrect ? "text-green-700 font-medium" : showResults && wasUserAnswer && !questionResult?.is_correct ? "text-red-700" : "text-slate-700"}`}>
                                    {option}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      
                      {/* Quiz Actions */}
                      <div className="flex gap-3 pt-4 border-t">
                        {!quizResult ? (
                          <Button 
                            onClick={submitQuiz} 
                            disabled={quizSubmitting}
                            className="flex-1"
                            data-testid="submit-quiz-btn"
                          >
                            {quizSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Submit Quiz
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button 
                            onClick={retakeQuiz}
                            variant="outline"
                            className="flex-1"
                            data-testid="retake-quiz-btn"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Retake Quiz
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="h-[400px] flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <GraduationCap className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">Select a training module</p>
                  <p className="text-sm mt-1">Choose a module from the list to start learning</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Quiz Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quizResult?.passed ? (
                <>
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  Congratulations!
                </>
              ) : (
                <>
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                  Keep Learning!
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {quizResult?.passed 
                ? "You've successfully completed this training module!"
                : "You need a bit more practice. Review the content and try again."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="text-center">
              <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${
                quizResult?.passed ? "bg-green-100" : "bg-amber-100"
              }`}>
                <span className={`text-3xl font-bold ${
                  quizResult?.passed ? "text-green-600" : "text-amber-600"
                }`}>
                  {quizResult?.score}%
                </span>
              </div>
              <p className="mt-4 text-slate-600">
                You got {quizResult?.correct_answers} out of {quizResult?.total_questions} questions correct
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Passing score: {quizResult?.passing_score}%
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Review Answers
            </Button>
            {quizResult?.passed ? (
              <Button onClick={() => {
                setShowResultDialog(false);
                setShowQuiz(false);
              }}>
                Continue Learning
              </Button>
            ) : (
              <Button onClick={retakeQuiz}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
